import type {IClassicBuilder, IClassicBuilderNode} from '@gl/builder';
import type {IAudienceBuilderNode} from '@gl/lit/types/builders/audience_builder/index.js';
import {AudienceBuilderNodeTypes} from '@gl/lit/types/builders/audience_builder/index.js';
import {FilterNode} from '@gl/lit/lib/builder/audience_builder/nodes/filter.js';
import {
  defaultFilterStemContraints,
  stemmableTypeMap,
} from '@gl/lit/common/constants/builders/audience_builder.js';
import {isFlex, isStem} from './filters.js';
import {isNumber} from '../index.js';
import type {Dataset} from '@gl/lit/types/datasets.js';
import {DatasetType} from '@gl/lit/types/datasets.js';

const parentTraversalMemoMap = new Map<string, IClassicBuilderNode[]>();
const childCountTraversalMemoMap = new Map<string, number>();

const traversalKey = (prefix: string, level: string) => {
  return `${prefix}__${level}`;
};

export function isAudienceBuilderNode(
  node: IClassicBuilderNode
): node is IAudienceBuilderNode & IClassicBuilderNode {
  return (node as unknown as IAudienceBuilderNode).isValid !== undefined;
}

/**
 * Determines whether a node is stemmable or not.
 */
export function isStemmableNode(node: IClassicBuilderNode): boolean {
  return (
    node.type === AudienceBuilderNodeTypes.FLEX ||
    node.type === AudienceBuilderNodeTypes.FILTER ||
    node.type === AudienceBuilderNodeTypes.STEM
  );
}

export function isAudienceNode(node: IClassicBuilderNode): boolean {
  return node.type === AudienceBuilderNodeTypes.AUDIENCE;
}

/**
 * Traverses depth-first through all parents, given a starting node.
 */
export function* traverseParents(
  startingNode: IClassicBuilderNode,
  builder: IClassicBuilder
): Generator<IClassicBuilderNode> {
  const parent = builder.getNodeFromCache(startingNode.level);

  if (parent) {
    yield parent;
    yield* traverseParents(parent, builder);
  }
}

export function getParents(
  startingNode: IClassicBuilderNode,
  builder: IClassicBuilder
): IClassicBuilderNode[] {
  const [version] = builder.history;

  const key = traversalKey(
    version?.timestamp.toString() ?? 'initial',
    startingNode.level
  );
  if (parentTraversalMemoMap.has(key)) {
    return parentTraversalMemoMap.get(key)!;
  }

  return [...traverseParents(startingNode, builder)];
}

export function getChildCount(
  startingNode: IClassicBuilderNode,
  builder: IClassicBuilder
): number {
  const [version] = builder.history;

  const key = traversalKey(
    version?.timestamp.toString() ?? 'initial',
    startingNode.level
  );
  if (childCountTraversalMemoMap.has(key)) {
    return childCountTraversalMemoMap.get(key)!;
  }

  try {
    return [
      ...traverseHierarchyDepth(startingNode.id, builder, id => {
        const node = builder.getNodeFromCache(id);
        if (!node) return true;
        return isFlex(node) || isStem(node);
      }),
    ].length;
  } catch (err) {
    console.error('Error fetching children', err);
    return 0;
  }
}

export function* traverseHierarchyDepth(
  key: string,
  builder: IClassicBuilder,
  predicate?: (id: string) => boolean
): Generator<string> {
  const level = builder.getLevelFromCache(key);
  const [first] = (level ?? ([] as string[])).filter(predicate ?? (() => true));

  if (first) {
    yield first;
    yield* traverseHierarchyDepth(first, builder);
  }
}

export function isPiiNode(
  node: IClassicBuilderNode,
  piiFields: string[]
): boolean {
  return !!(
    isAudienceBuilderNode(node) &&
    node instanceof FilterNode &&
    piiFields.length > 0 &&
    piiFields.some(f => node.hasField(f))
  );
}

export function filterStemConstraints(
  targetNode: IClassicBuilderNode,
  builder: IClassicBuilder,
  opts?: {nestLimit: number}
) {
  const parentCount = getParents(targetNode, builder).length;
  // The root is considered 1 level; a flexNode is its own level.
  const limitOffset = isFlex(targetNode) ? 2 : 1;

  if (
    !opts?.nestLimit ||
    (opts?.nestLimit && parentCount < opts.nestLimit - limitOffset)
  ) {
    return defaultFilterStemContraints;
  }

  return new Set(
    Array.from(defaultFilterStemContraints).filter(type => {
      return type !== AudienceBuilderNodeTypes.FLEX;
    })
  );
}

export function flexStemConstraints(
  targetNode: IClassicBuilderNode,
  builder: IClassicBuilder,
  opts?: {nestLimit: number}
) {
  const parentCount = getParents(targetNode, builder).length;
  // The root is considered 1 level; a flexNode is its own level.
  const limitOffset = isFlex(targetNode) ? 2 : 1;

  if (
    !opts?.nestLimit ||
    (opts?.nestLimit && parentCount < opts.nestLimit - limitOffset)
  ) {
    return defaultFilterStemContraints;
  }

  return new Set([AudienceBuilderNodeTypes.FLEX]);
}

export function isStemmableNeighbor(
  target: IClassicBuilderNode,
  source: IClassicBuilderNode
): boolean {
  if (!isAudienceBuilderNode(target) || !isAudienceBuilderNode(source)) {
    throw Error('This util only works for audience builder nodes');
  }

  return Boolean(
    stemmableTypeMap
      .get(target.type as AudienceBuilderNodeTypes)
      ?.includes(source.type as AudienceBuilderNodeTypes) &&
      stemmableTypeMap
        .get(source.type as AudienceBuilderNodeTypes)
        ?.includes(target.type as AudienceBuilderNodeTypes)
  );
}

/**
 * Given a node, git the nearest flex parent's dataset ID.
 * If there is none, return the primary dataset ID.
 */
export function getParentDatasetId(
  node: IClassicBuilderNode,
  builder: IClassicBuilder & {datasets: Dataset[]}
): number {
  for (const parent of traverseParents(node, builder)) {
    if ('datasetId' in parent) {
      return isNumber(parent.datasetId) ? parent.datasetId : 0;
    }
  }

  return (
    builder.datasets.find(d => {
      return d.dataset_type === DatasetType.USER;
    })?.id || 0
  );
}
